/**
=========================================================
* Soft UI Dashboard React - v4.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useMemo } from "react";

// porp-types is a library for typechecking of props
import PropTypes from "prop-types";

// react-chartjs-2 components
import { Bar } from "react-chartjs-2";

// @mui material components
import Card from "@mui/material/Card";

// Soft UI Dashboard React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";

// HorizontalBarChart configurations
import configs from "examples/Charts/BarCharts/HorizontalBarChart/configs";

// Soft UI Dashboard React base styles
import colors from "assets/theme/base/colors";

function HorizontalBarChart({ title, description, height, chart, showLegend, variant }) {
  const chartDatasets = chart.datasets
    ? chart.datasets.map((dataset) => ({
        ...dataset,
        weight: 5,
        borderWidth: 0,
        borderRadius: variant === "report" ? 6 : 4,
        backgroundColor: colors[dataset.color]
          ? colors[dataset.color || "dark"].main
          : colors.dark.main,
        fill: false,
        maxBarThickness: variant === "report" ? 28 : 35,
      }))
    : [];

  const base = configs(chart.labels || [], chartDatasets);
  const reportOptions =
    variant === "report"
      ? {
          scales: {
            x: {
              grid: {
                drawBorder: false,
                display: true,
                drawOnChartArea: true,
                color: "rgba(148, 163, 184, 0.35)",
              },
              ticks: {
                color: "#64748b",
                font: { size: 11 },
              },
            },
            y: {
              grid: {
                drawBorder: false,
                display: true,
                drawOnChartArea: true,
                color: "rgba(226, 232, 240, 0.9)",
                borderDash: [4, 4],
              },
              ticks: {
                color: "#475569",
                font: { size: 11 },
              },
            },
          },
          plugins: {
            tooltip: {
              backgroundColor: "rgba(15, 23, 42, 0.92)",
              titleFont: { size: 12, weight: "600" },
              bodyFont: { size: 12 },
              padding: 12,
              cornerRadius: 8,
              displayColors: true,
            },
          },
        }
      : {};

  const { data, options } = {
    ...base,
    options: {
      ...base.options,
      scales: {
        ...base.options.scales,
        ...(variant === "report" ? reportOptions.scales : {}),
        x: {
          ...base.options.scales.x,
          ...(variant === "report" ? reportOptions.scales?.x : {}),
          grid: {
            ...base.options.scales.x.grid,
            ...(variant === "report" ? reportOptions.scales?.x?.grid : {}),
          },
          ticks: {
            ...base.options.scales.x.ticks,
            ...(variant === "report" ? reportOptions.scales?.x?.ticks : {}),
          },
        },
        y: {
          ...base.options.scales.y,
          ...(variant === "report" ? reportOptions.scales?.y : {}),
          grid: {
            ...base.options.scales.y.grid,
            ...(variant === "report" ? reportOptions.scales?.y?.grid : {}),
          },
          ticks: {
            ...base.options.scales.y.ticks,
            ...(variant === "report" ? reportOptions.scales?.y?.ticks : {}),
          },
        },
      },
      plugins: {
        ...base.options.plugins,
        ...(variant === "report" ? reportOptions.plugins : {}),
        legend: {
          ...(base.options.plugins?.legend || {}),
          display: Boolean(showLegend),
          position: "bottom",
          labels: {
            ...(base.options.plugins?.legend?.labels || {}),
            usePointStyle: true,
            padding: variant === "report" ? 16 : undefined,
            font: variant === "report" ? { size: 12 } : undefined,
          },
        },
        tooltip: {
          ...(base.options.plugins?.tooltip || {}),
          ...(variant === "report" ? reportOptions.plugins?.tooltip : {}),
        },
      },
    },
  };

  const renderChart = (
    <SoftBox p={2}>
      {title || description ? (
        <SoftBox px={description ? 1 : 0} pt={description ? 1 : 0}>
          {title && (
            <SoftBox mb={1}>
              <SoftTypography variant="h6">{title}</SoftTypography>
            </SoftBox>
          )}
          <SoftBox mb={2}>
            <SoftTypography component="div" variant="button" fontWeight="regular" color="text">
              {description}
            </SoftTypography>
          </SoftBox>
        </SoftBox>
      ) : null}
      {useMemo(
        () => (
          <SoftBox height={height}>
            <Bar data={data} options={options} />
          </SoftBox>
        ),
        [data, options, height, variant]
      )}
    </SoftBox>
  );

  return title || description ? <Card>{renderChart}</Card> : renderChart;
}

// Setting default values for the props of HorizontalBarChart
HorizontalBarChart.defaultProps = {
  title: "",
  description: "",
  height: "19.125rem",
  showLegend: false,
  variant: "default",
};

// Typechecking props for the HorizontalBarChart
HorizontalBarChart.propTypes = {
  title: PropTypes.string,
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  chart: PropTypes.objectOf(PropTypes.array).isRequired,
  showLegend: PropTypes.bool,
  variant: PropTypes.oneOf(["default", "report"]),
};

export default HorizontalBarChart;
